package org.example.project.client

import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import org.example.project.client.api.AuthApiClient
import org.example.project.client.api.DefaultAuthApiClient
import org.example.project.client.config.ApiConfig
import org.example.project.client.model.ClientType
import org.example.project.client.session.DefaultSessionRepository
import org.example.project.client.session.SessionRepository
import org.example.project.client.session.SessionStorage

object JsApiClientFactory {

    fun createHttpClient(
        configureEngine: (io.ktor.client.engine.js.JsClientEngineConfig.() -> Unit)? = null
    ): HttpClient {
        return HttpClient(Js) {
            engine {
                configureRequest {
                    credentials = "include"
                    redirect = "error"
                }
                configureEngine?.invoke(this)
            }
            install(HttpTimeout) {
                requestTimeoutMillis = 15_000L
                connectTimeoutMillis = 10_000L
                socketTimeoutMillis = 15_000L
            }
            install(ContentNegotiation) {
                json(Json {
                    ignoreUnknownKeys = true
                    isLenient = false
                })
            }
            expectSuccess = false
            followRedirects = false
        }
    }

    fun createSessionRepository(
        apiConfig: ApiConfig,
        customHttpClient: HttpClient? = null,
        customSessionStorage: SessionStorage? = null
    ): SessionRepository {
        val ownsClient = customHttpClient == null
        val httpClient = customHttpClient ?: createHttpClient()
        val storage = customSessionStorage ?: JsSessionStorage()
        val authApiClient: AuthApiClient = DefaultAuthApiClient(httpClient, apiConfig)

        return DefaultSessionRepository(
            authApiClient = authApiClient,
            sessionStorage = storage,
            apiConfig = apiConfig,
            clientType = ClientType.WEB,
            httpClientToClose = if (ownsClient) httpClient else null
        )
    }

    fun createActivityApiClient(
        apiConfig: ApiConfig,
        customHttpClient: HttpClient? = null,
        configureEngine: (io.ktor.client.engine.js.JsClientEngineConfig.() -> Unit)? = null
    ): org.example.project.client.api.ActivityApiClient {
        val ownsHttpClient = customHttpClient == null
        val httpClient = customHttpClient ?: createHttpClient(configureEngine)
        return org.example.project.client.api.DefaultActivityApiClient(
            httpClient = httpClient,
            apiConfig = apiConfig,
            ownsHttpClient = ownsHttpClient
        )
    }

    fun createPlannerApiClient(
        apiConfig: ApiConfig,
        customHttpClient: HttpClient? = null,
        configureEngine: (io.ktor.client.engine.js.JsClientEngineConfig.() -> Unit)? = null
    ): org.example.project.client.api.PlannerApiClient {
        val ownsHttpClient = customHttpClient == null
        val httpClient = customHttpClient ?: createHttpClient(configureEngine)
        return org.example.project.client.api.DefaultPlannerApiClient(
            httpClient = httpClient,
            apiConfig = apiConfig,
            ownsHttpClient = ownsHttpClient
        )
    }

    fun createPlannerRepository(
        sessionRepository: SessionRepository,
        apiConfig: ApiConfig,
        customHttpClient: HttpClient? = null
    ): org.example.project.planner.PlannerRepository {
        val plannerApiClient = createPlannerApiClient(apiConfig, customHttpClient)
        return org.example.project.planner.DefaultPlannerRepository(plannerApiClient, sessionRepository)
    }
}
